import type {CompRef, InnerElementPath} from '@wix/document-services-types'
import type {DmApis, ExtensionAPI, DAL} from '@wix/document-manager-core'
import type {RelationshipsAPI} from '../relationships'
import type {InnerElementsApi, InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import {DATA_TYPES} from '../../constants/constants'
import {stripHashIfExists} from '../../utils/refArrayUtils'

interface TreeWalkVisitor {
    onComponent(compRef: CompRef): void
    onInnerElement(compRef: CompRef, path: InnerElementPath, innerElementId: string): void
    shouldStopAtComponent?(compRef: CompRef): boolean
}

const collectInnerElementPaths = (
    compRef: CompRef,
    innerElements: InnerElementsApi,
    basePath: InnerElementPath = []
): {path: InnerElementPath; innerElementId: string}[] => {
    const names = innerElements.getInnerElementsNames(compRef, basePath)
    return names.flatMap(name => {
        const path = [...basePath, name]
        const item = innerElements.getInnerElement(compRef, path)
        const entry = item?.id ? [{path, innerElementId: item.id as string}] : []
        return [...entry, ...collectInnerElementPaths(compRef, innerElements, path)]
    })
}

const walkSubtree = (
    root: CompRef,
    pointers: DmApis['pointers'],
    extensionAPI: ExtensionAPI,
    visitor: TreeWalkVisitor
): void => {
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI

    visitor.onComponent(root)

    for (const {path, innerElementId} of collectInnerElementPaths(root, innerElements)) {
        visitor.onInnerElement(root, path, innerElementId)
    }

    const children = pointers.structure.getChildren(root) as CompRef[]
    for (const child of children) {
        if (visitor.shouldStopAtComponent?.(child)) {
            continue
        }
        walkSubtree(child, pointers, extensionAPI, visitor)
    }
}

const getOwningComponentForContextConfig = (
    contextConfigPointer: {id: string; type: string},
    extensionAPI: RelationshipsAPI
): CompRef | undefined => {
    const {relationships} = extensionAPI
    const [ctxListPointer] = relationships.getReferencesToPointer(contextConfigPointer, DATA_TYPES.contexts)
    if (!ctxListPointer) return undefined
    const [compRef] = relationships.getReferencesToPointer(ctxListPointer)
    return compRef as CompRef | undefined
}

const getInnerElementPathAndOwner = (
    innerElementId: string,
    extensionAPI: RelationshipsAPI,
    dal: DAL
): {path: InnerElementPath; compRef: CompRef | undefined} => {
    const {relationships} = extensionAPI
    const pathSegments: string[] = []
    let currentId = innerElementId

    let [parentPointer] = relationships.getReferencesToPointer(
        {id: currentId, type: DATA_TYPES.innerElements},
        DATA_TYPES.innerElements
    )
    while (parentPointer) {
        const parentItem = dal.get(parentPointer)
        const innerElementsMap = parentItem?.innerElements ?? {}

        for (const [name, ref] of Object.entries(innerElementsMap) as [string, string][]) {
            if (stripHashIfExists(ref) === currentId) {
                pathSegments.unshift(name)
                break
            }
        }

        currentId = parentPointer.id
        ;[parentPointer] = relationships.getReferencesToPointer(
            {id: currentId, type: DATA_TYPES.innerElements},
            DATA_TYPES.innerElements
        )
    }

    const [compRef] = relationships.getReferencesToPointer({id: currentId, type: DATA_TYPES.innerElements})
    return {path: pathSegments, compRef: compRef as CompRef | undefined}
}

export type {TreeWalkVisitor}
export {walkSubtree, getOwningComponentForContextConfig, getInnerElementPathAndOwner}
